import React, {useState} from "react";
import * as axios from "axios";
import {Dialog, IconButton, Button, TextField} from "@material-ui/core";
import {Autocomplete} from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import {CAR_MAKES, modelsForMake} from "./carData";
import "./styles.css";

const baseUrl = 'https://api.appetito.ro/';

const emptyForm = {make: '', model: '', plateNumber: '', rompetrolPin: '', itpExpiresAt: '', rcaExpiresAt: '', rovinietaExpiresAt: ''};

// Parses a "YYYY-MM-DD..." date string (what the backend sends) down to
// just the date part, for the <input type="date"> value.
function dateOnly(value) {
  return (value || '').slice(0, 10);
}

function formFromCar(car) {
  if (!car) {
    return emptyForm;
  }
  return {
    make: car.make || '',
    model: car.model || '',
    plateNumber: car.plate_number || '',
    rompetrolPin: car.rompetrol_pin || '',
    itpExpiresAt: dateOnly(car.itp_expires_at),
    rcaExpiresAt: dateOnly(car.rca_expires_at),
    rovinietaExpiresAt: dateOnly(car.rovinieta_expires_at),
  };
}

// Make/model suggestions come from a bundled static list (./carData) — a
// live external API (NHTSA) was tried first but proved unreachable from
// every environment tested here. Both fields stay freeSolo, so anything not
// in the list can still be typed directly.
//
// car === null means "create new"; an object means "edit this one". Cars.js
// keys this component on `${car.id}-${open}` — a fresh instance every time
// the dialog opens, rather than one long-lived instance whose form state
// gets resynced via an effect — so there's no window where a mid-transition
// open (e.g. clicking Edit on another row right after Anulează) can leave
// the previous car's data showing. That's why form/error init straight from
// props instead of an effect: this component is guaranteed to be freshly
// mounted for the car/open combination it's rendering.
export default function CarModal({open, car, onClose, onSaved, onFileUploaded}) {
  const isEditing = !!car;
  const [form, setForm] = useState(() => formFromCar(car));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [rcaFileUrl, setRcaFileUrl] = useState(car ? car.rca_file_url || '' : '');
  const [cascoFileUrl, setCascoFileUrl] = useState(car ? car.casco_file_url || '' : '');
  const [rcaUploading, setRcaUploading] = useState(false);
  const [cascoUploading, setCascoUploading] = useState(false);

  const modelOptions = modelsForMake(form.make);

  const save = async () => {
    if (!form.make || !form.model || !form.plateNumber) {
      setError('Marcă, model și număr de înmatriculare sunt obligatorii.');
      return;
    }
    if (form.rompetrolPin && !/^\d{4}$/.test(form.rompetrolPin)) {
      setError('PIN-ul Rompetrol trebuie să aibă 4 cifre.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const payload = {
        make: form.make,
        model: form.model,
        plate_number: form.plateNumber,
        rompetrol_pin: form.rompetrolPin,
        itp_expires_at: form.itpExpiresAt,
        rca_expires_at: form.rcaExpiresAt,
        rovinieta_expires_at: form.rovinietaExpiresAt,
      };
      const response = isEditing
        ? await axios.default.put(`${baseUrl}v1/admin/cars/${car.id}`, payload, {
          headers: {Authorization: `Bearer ${token}`},
        })
        : await axios.default.post(`${baseUrl}v1/admin/cars`, payload, {
          headers: {Authorization: `Bearer ${token}`},
        });
      onSaved(response.data);
    } catch (e) {
      setError('Eroare la salvare.');
    } finally {
      setSaving(false);
    }
  };

  // RCA/CASCO upload immediately, independent of Salvează — same pattern as
  // restaurant logo/cover-photo upload elsewhere: read the file, base64
  // it, POST it, done. Each just overwrites the car's stored URL, no
  // history kept (see the backend's aws.UploadCarDocument).
  const uploadDocument = (docType, setUrl, setUploading) => async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    const extension = (file.name.split('.').pop() || '').toLowerCase();

    setUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const token = localStorage.getItem('access_token');
      const response = await axios.default.post(
        `${baseUrl}v1/admin/cars/${car.id}/${docType}-file`,
        {image: base64, extension},
        {headers: {Authorization: `Bearer ${token}`}},
      );
      setUrl(response.data.url);
      if (onFileUploaded) {
        onFileUploaded();
      }
    } catch (e) {
      alert('Eroare la încărcarea fișierului.');
    } finally {
      setUploading(false);
    }
  };

  const uploadRcaFile = uploadDocument('rca', setRcaFileUrl, setRcaUploading);
  const uploadCascoFile = uploadDocument('casco', setCascoFileUrl, setCascoUploading);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <div className="deliverer-modal">
        <div className="deliverer-modal-topbar">
          <h3 className="deliverer-modal-title">{isEditing ? 'Editează mașină' : 'Adaugă mașină'}</h3>
          <IconButton className="deliverer-modal-close" size="small" onClick={onClose}>
            <CloseIcon/>
          </IconButton>
        </div>

        <div className="deliverer-modal-fields">
          <Autocomplete
            freeSolo
            options={CAR_MAKES}
            // Both value and inputValue are controlled — with only inputValue
            // controlled, MUI's mount-time (and post-selection) "reset" sync
            // had nothing correct to derive from and blanked the field; with
            // value also controlled, that reset now derives from the right
            // thing. onChange (option clicked/selected) and onInputChange
            // reason "input" (actual typing) both count as real changes;
            // other onInputChange reasons (including "reset") are MUI's own
            // housekeeping, not user intent.
            value={form.make}
            inputValue={form.make}
            onChange={(event, value) => setForm((prev) => ({...prev, make: value || '', model: ''}))}
            onInputChange={(event, value, reason) => {
              if (reason !== 'input') {
                return;
              }
              setForm((prev) => ({...prev, make: value, model: ''}));
            }}
            renderInput={(params) => <TextField {...params} label="Marcă" margin="dense" fullWidth/>}
          />
          <Autocomplete
            freeSolo
            options={modelOptions}
            value={form.model}
            inputValue={form.model}
            onChange={(event, value) => setForm((prev) => ({...prev, model: value || ''}))}
            onInputChange={(event, value, reason) => {
              if (reason !== 'input') {
                return;
              }
              setForm((prev) => ({...prev, model: value}));
            }}
            renderInput={(params) => <TextField {...params} label="Model" margin="dense" fullWidth/>}
          />
          <TextField
            label="Număr de înmatriculare"
            value={form.plateNumber}
            onChange={(event) => setForm({...form, plateNumber: event.target.value.toUpperCase()})}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Pin Rompetrol"
            value={form.rompetrolPin}
            onChange={(event) => setForm({...form, rompetrolPin: event.target.value.replace(/\D/g, '').slice(0, 4)})}
            fullWidth
            margin="dense"
            placeholder="4 cifre"
            inputProps={{inputMode: 'numeric', maxLength: 4}}
          />
          <TextField
            label="Expiră ITP"
            type="date"
            value={form.itpExpiresAt}
            onChange={(event) => setForm({...form, itpExpiresAt: event.target.value})}
            fullWidth
            margin="dense"
            InputLabelProps={{shrink: true}}
          />
          <TextField
            label="Expiră RCA"
            type="date"
            value={form.rcaExpiresAt}
            onChange={(event) => setForm({...form, rcaExpiresAt: event.target.value})}
            fullWidth
            margin="dense"
            InputLabelProps={{shrink: true}}
          />
          <TextField
            label="Expiră rovinietă"
            type="date"
            value={form.rovinietaExpiresAt}
            onChange={(event) => setForm({...form, rovinietaExpiresAt: event.target.value})}
            fullWidth
            margin="dense"
            InputLabelProps={{shrink: true}}
          />

          {isEditing && (
            <div style={{display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <span style={{minWidth: 60, color: '#666', fontSize: 13}}>RCA</span>
                {rcaFileUrl && (
                  <a href={rcaFileUrl} target="_blank" rel="noreferrer" style={{fontSize: 13}}>
                    Vezi fișier
                  </a>
                )}
                <label style={{cursor: 'pointer', color: '#DA7D46', fontSize: 13, fontWeight: 600}}>
                  <input type="file" accept="application/pdf,image/*" style={{display: 'none'}}
                         onChange={uploadRcaFile} disabled={rcaUploading}/>
                  {rcaUploading ? 'Se încarcă...' : (rcaFileUrl ? 'Înlocuiește' : 'Încarcă')}
                </label>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <span style={{minWidth: 60, color: '#666', fontSize: 13}}>CASCO</span>
                {cascoFileUrl && (
                  <a href={cascoFileUrl} target="_blank" rel="noreferrer" style={{fontSize: 13}}>
                    Vezi fișier
                  </a>
                )}
                <label style={{cursor: 'pointer', color: '#DA7D46', fontSize: 13, fontWeight: 600}}>
                  <input type="file" accept="application/pdf,image/*" style={{display: 'none'}}
                         onChange={uploadCascoFile} disabled={cascoUploading}/>
                  {cascoUploading ? 'Se încarcă...' : (cascoFileUrl ? 'Înlocuiește' : 'Încarcă')}
                </label>
              </div>
            </div>
          )}
        </div>

        {error && <div className="deliverer-modal-error">{error}</div>}

        <div className="deliverer-modal-footer">
          <Button onClick={onClose} disabled={saving}>Anulează</Button>
          <Button onClick={save} disabled={saving} variant="contained" color="primary">
            Salvează
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
